import { bisector } from 'd3-array';
import { pointer } from 'd3-selection';
import { convert } from './utils.js';

const bisectPress = bisector((d) => d.press).left;

// Skysight pillbox styles (decoded from bundle):
//   temp-pillbox / dwpt-pillbox / wspd-pillbox:
//     rx=2 ry=2, fill rgba(255,255,255,0.85), stroke rgba(200,200,200,0.6) 0.5
//   height-pillbox / pressure-pillbox:
//     rx=3 ry=3, fill #e0e0e0, stroke #999 0.5
//   All texts: font-size 11px, dy 0.35em, text-anchor end (mostly).
const VALUE_PILLBOX = {
    rx: 2,
    ry: 2,
    fill: 'rgba(255, 255, 255, 0.85)',
    stroke: 'rgba(200, 200, 200, 0.6)',
    strokeWidth: 0.5,
};
const AXIS_PILLBOX = {
    rx: 3,
    ry: 3,
    fill: '#e0e0e0',
    stroke: '#999',
    strokeWidth: 0.5,
};

/**
 * Apply Skysight-style pillbox + text styling to a focus group.
 * The pillbox is inserted BEFORE the text so it sits underneath.
 * Returns a function that resizes the pillbox to wrap the current text.
 */
function wrapWithPillbox(group, pillStyle, padX = 4, padY = 2) {
    const pill = group
        .insert('rect', 'text')
        .attr('rx', pillStyle.rx)
        .attr('ry', pillStyle.ry)
        .style('fill', pillStyle.fill)
        .style('stroke', pillStyle.stroke)
        .style('stroke-width', pillStyle.strokeWidth);
    return () => {
        const textNode = group.select('text').node();
        if (!textNode) return;
        let bb;
        try {
            bb = textNode.getBBox();
        } catch (_) {
            return;
        }
        pill
            .attr('x', bb.x - padX)
            .attr('y', bb.y - padY)
            .attr('width', bb.width + padX * 2)
            .attr('height', bb.height + padY * 2);
    };
}

/**
 * Create interactive tooltip overlay for a sounding line.
 *
 * Skysight-style: each value is wrapped in a small white pillbox; altitude
 * and pressure read out at the axis edges in light-grey pillboxes; wind
 * tooltip shows direction plus speed.
 *
 * @param {import('d3-selection').Selection} skewtgroup - Group containing skew-T data lines
 * @param {import('d3-selection').Selection} container - Parent container for the overlay rect
 * @param {import('./scales.js').Scales} scales
 * @param {Array<{press:number, hght:number, temp:number, dwpt:number, wdir:number, wspd:number}>} skewtline - Sounding data points (not mutated)
 * @param {'kt'|'kmh'|'ms'} unit - Display unit for wind speed
 */
export function drawToolTips(skewtgroup, container, scales, skewtline, unit) {
    const { w, x, y, skewX, tMax, tMin } = scales;

    // Focus groups must paint ABOVE all extension groups (altitude axis ticks,
    // pressure axis ticks, static pillboxes like "Top of Thermals"/"LCL").
    // Appending to `container` puts them at the END of the container's DOM,
    // which is repainted on top in SVG. The lib's #clearPlot removes
    // `.skewt-overlay` from container before each redraw; we also remove any
    // prior `.skewt-focus` from container here so re-renders don't leak.
    container.selectAll('.skewt-focus').remove();
    const focusGroup = container;

    // Pressure ascending for bisector (don't mutate the caller's array)
    const lines = [...skewtline].reverse();

    // Wind-line x position formula — mirrors render-wind-line.js so the wind
    // tooltip pillbox sits ON the wind curve, not pinned to the right axis.
    //   xOfWspd(w) = scales.x(tMax - range + w / tMult)
    //   tMult = scaleMax / range,  range = (tMax - tMin)/5
    //   scaleMax = max(5, ceil(maxDataWspd / 5) * 5)
    const range = (tMax - tMin) / 5;
    const maxDataWspd = lines.reduce(
        (m, d) => (Number.isFinite(d.wspd) ? Math.max(m, d.wspd) : m),
        0,
    );
    const scaleMax = Math.max(5, Math.ceil(maxDataWspd / 5) * 5);
    const tMult = scaleMax / range;
    const xOfWspd = (wspd) => x(tMax - range + wspd / tMult);

    // ── Temperature focus (red) ────────────────────────────────────────
    const tmpcfocus = focusGroup
        .append('g')
        .attr('class', 'skewt-focus skewt-tmpc')
        .style('display', 'none');
    tmpcfocus.append('circle').attr('r', 4);
    const tmpcText = tmpcfocus
        .append('text')
        .attr('x', 9)
        .attr('dy', '.35em')
        .style('font-size', '11px')
        .style('fill', '#333');
    tmpcfocus.select('text').lower();
    const tmpcPill = wrapWithPillbox(tmpcfocus, VALUE_PILLBOX);

    // ── Dewpoint focus (green) ─────────────────────────────────────────
    const dwpcfocus = focusGroup
        .append('g')
        .attr('class', 'skewt-focus skewt-dwpc')
        .style('display', 'none');
    dwpcfocus.append('circle').attr('r', 4);
    dwpcfocus
        .append('text')
        .attr('x', -9)
        .attr('text-anchor', 'end')
        .attr('dy', '.35em')
        .style('font-size', '11px')
        .style('fill', '#333');
    const dwpcPill = wrapWithPillbox(dwpcfocus, VALUE_PILLBOX);

    // ── Altitude pillbox at LEFT axis ──────────────────────────────────
    const hghtfocus = focusGroup
        .append('g')
        .attr('class', 'skewt-focus skewt-height-focus')
        .style('display', 'none');
    hghtfocus
        .append('text')
        .attr('x', -3)
        .attr('text-anchor', 'end')
        .attr('dy', '.35em')
        .style('fill', '#333')
        .style('font-size', '11px');
    const hghtPill = wrapWithPillbox(hghtfocus, AXIS_PILLBOX, 4, 2);

    // ── Pressure pillbox at RIGHT axis ─────────────────────────────────
    const pressfocus = focusGroup
        .append('g')
        .attr('class', 'skewt-focus skewt-pressure-focus')
        .style('display', 'none');
    pressfocus
        .append('text')
        .attr('x', 24)
        .attr('text-anchor', 'end')
        .attr('dy', '.35em')
        .style('fill', '#333')
        .style('font-size', '11px');
    const pressPill = wrapWithPillbox(pressfocus, AXIS_PILLBOX, 4, 2);

    // ── Wind focus (blue) — circle + dir°+speed pillbox ────────────────
    const wspdfocus = focusGroup
        .append('g')
        .attr('class', 'skewt-focus skewt-wspc skewt-windspeed')
        .style('display', 'none');
    wspdfocus.append('circle').attr('r', 4).style('fill', '#1e3a8a');
    wspdfocus
        .append('text')
        .attr('x', -9)
        .attr('text-anchor', 'end')
        .attr('dy', '.35em')
        .style('font-size', '11px')
        .style('fill', '#333');
    const wspdPill = wrapWithPillbox(wspdfocus, VALUE_PILLBOX);

    // ── Overlay rect for mouse events ──────────────────────────────────
    container
        .append('rect')
        .attr('class', 'skewt-overlay')
        .attr('width', w)
        .attr('height', scales.h)
        .on('mouseover', () => {
            tmpcfocus.style('display', 'block');
            dwpcfocus.style('display', 'block');
            hghtfocus.style('display', 'block');
            pressfocus.style('display', 'block');
            wspdfocus.style('display', 'block');
        })
        .on('mouseout', () => {
            tmpcfocus.style('display', 'none');
            dwpcfocus.style('display', 'none');
            hghtfocus.style('display', 'none');
            pressfocus.style('display', 'none');
            wspdfocus.style('display', 'none');
        })
        .on('mousemove', (event) => {
            // Smooth hover (matches Skysight): linearly interpolate the
            // sounding's temp/dwpt/wspd/wdir/hght between the two adjacent
            // data points at the cursor's exact pressure. The lib previously
            // snapped to the closer of (d0, d1) which produced visible
            // "stepping" when the data has ~50 levels.
            const y0 = y.invert(pointer(event)[1]);
            const i = bisectPress(lines, y0, 1, lines.length - 1);
            const d0 = lines[i - 1];
            const d1 = lines[i];
            let d;
            if (Math.abs(d1.press - d0.press) < 0.001) {
                d = d0;
            } else {
                const v = (y0 - d0.press) / (d1.press - d0.press);
                d = {
                    press: y0,
                    temp: d0.temp + v * (d1.temp - d0.temp),
                    dwpt: d0.dwpt + v * (d1.dwpt - d0.dwpt),
                    wspd: d0.wspd + v * (d1.wspd - d0.wspd),
                    wdir: d0.wdir + v * (d1.wdir - d0.wdir),
                    hght: d0.hght + v * (d1.hght - d0.hght),
                };
            }
            const yp = y(d.press);

            // Positions (anchor each group at its target point)
            tmpcfocus.attr('transform', `translate(${skewX(d.temp, d.press)},${yp})`);
            dwpcfocus.attr('transform', `translate(${skewX(d.dwpt, d.press)},${yp})`);
            hghtfocus.attr('transform', `translate(0,${yp})`);
            pressfocus.attr('transform', `translate(${w},${yp})`);
            const wxAtCursor = Number.isFinite(d.wspd) ? xOfWspd(d.wspd) : w;
            wspdfocus.attr('transform', `translate(${wxAtCursor},${yp})`);

            // Values
            tmpcfocus.select('text').text(`${Math.round(d.temp)}°C`);
            dwpcfocus.select('text').text(`${Math.round(d.dwpt)}°C`);
            hghtfocus.select('text').text(`${Math.round(d.hght)}m`);
            pressfocus.select('text').text(`${Math.round(d.press)}hPa`);
            const spd = Math.round(convert(d.wspd, unit) * 10) / 10;
            const dir = Number.isFinite(d.wdir) ? `${Math.round(d.wdir)}°` : '';
            wspdfocus.select('text').text(dir ? `${dir} ${spd}${unit}` : `${spd}${unit}`);

            // Resize pillboxes to wrap the text
            tmpcPill();
            dwpcPill();
            hghtPill();
            pressPill();
            wspdPill();
        });
}
